<template>
  <v-content class="client candidate-search page">
    <v-container>
      <v-layout column>
        <v-toolbar flat color="transparent" class="mb-4">
          <v-toolbar-title>Candidates</v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            color="primary"
            prepend-icon="mdi-magnify"
            placeholder="Search"
            class="pb-3"
            hide-details
            single-line
            @keypress.enter="submitSearch"
            @input="submitSearch"
            clearable
          />
        </v-toolbar>

        <div
          v-for="(application, i) in applications"
          :key="`application-${application.id}`"
          class="ml-4"
          :style="`opacity: ${canEditApplication(application) ? 1 : 0.6}`"
        >
          <v-card class="my-2 ml-4">
            <v-card-title
              :style="`${
                canEditApplication(application) ? 'cursor: pointer;' : ''
              }`"
              @click="
                canEditApplication(application) && toggleView(application.id)
              "
            >
              <v-layout align-center>
                <div class="mr-2">
                  <v-avatar
                    size="88"
                    color="muted"
                    style="position: absolute; top: 0px; left: -36px"
                  >
                    <v-avatar
                      size="84"
                      color="white"
                      :class="{
                        'v-avatar--grayscale': !canEditApplication(application),
                      }"
                    >
                      <img
                        v-if="application.profile && application.profile.avatar"
                        :src="application.profile.avatar"
                        v-img-orientation-changer
                      />
                      <img v-else src="/img/missing/avatars/original.png" />
                    </v-avatar>
                  </v-avatar>
                </div>

                <div
                  class="mr-2 truncate"
                  style="padding-left: 3rem"
                  slot="activator"
                >
                  <div class="subheading">
                    <span class="fade-out-right pr-4">
                      {{ application.profile.firstName }}
                      {{ application.profile.lastName }}
                      <v-chip
                        small
                        :class="{
                          'green darken-3 white--text':
                            application.status == 'Successful',
                          'red darken-3 white--text':
                            application.status == 'Unsuccessful',
                        }"
                      >
                        {{ application.status }}
                      </v-chip>
                    </span>
                  </div>
                  <div class="muted--text">
                    <span class="fade-out-right pr-4">
                      {{ application.opportunity.name }}
                    </span>
                  </div>
                </div>

                <v-spacer />

                <div class="d-flex text-no-wrap" style="z-index: 1">
                  <v-btn
                    :style="`${
                      canEditApplication(application) ? '' : 'cursor: default;'
                    }`"
                    @click="
                      canEditApplication(application) &&
                        toggleView(application.id)
                    "
                    flat
                    class="pr-4"
                  >
                    <v-icon color="muted" left>mdi-account</v-icon>
                    <span class="caption muted--text"> View </span>
                  </v-btn>

                  <v-btn
                    class="pr-4"
                    :style="`${
                      canEditApplication(application) ? '' : 'cursor: default;'
                    }`"
                    flat
                    @click.stop="
                      canEditApplication(application) &&
                        newTab(
                          `/client/opportunities/${application.opportunity.id}/applications?view=${application.id}&status=${application.status}`
                        )
                    "
                  >
                    <v-icon color="muted" left>mdi-account-group</v-icon>
                    <span class="caption muted--text"> View in workspace </span>
                  </v-btn>

                  <v-menu
                    offset-y
                    left
                    open-on-hover
                    v-if="canEditApplication(application)"
                  >
                    <span
                      slot="activator"
                      class="pt-2 pr-2"
                      style="pointer: default"
                    >
                      <v-icon
                        right
                        :color="themes.client.light.muted"
                        @click.stop=""
                        style="pointer: default"
                      >
                        mdi-dots-vertical
                      </v-icon>
                    </span>

                    <ApplicationEventsList
                      :opportunity="application.opportunity"
                      :applications="application"
                      include-regret
                    />
                  </v-menu>
                  <v-icon right :color="themes.client.light.muted" v-else>
                    mdi-dots-vertical
                  </v-icon>
                </div>
              </v-layout>
            </v-card-title>
          </v-card>
        </div>
        <v-card v-if="!applications[0]">
          <v-card-text>
            <v-alert
              outline
              :color="themes.client.light.tertiary"
              type="info"
              :value="true"
            >
              <span class="subheading" v-if="applicationsLoading">
                <v-progress-circular
                  class="pr-2"
                  indeterminate
                ></v-progress-circular>
                Searching for applications
              </span>
              <span class="subheading" v-else-if="search && search.length">
                No applications match the search term. Please check you spelling
                or try a different search term.
              </span>
              <span class="subheading" v-else>
                Search for applications by providing a name, surname, email
                address or identification number.
              </span>
            </v-alert>
          </v-card-text>
        </v-card>
        <v-card flat color="transparent" v-if="multipleRegrettable">
          <v-card-text class="text-xs-right" v-if="promptingRegret">
            Are you sure you want to mark all the above applications as
            <em>Unsuccessful</em>?
          </v-card-text>
          <v-card-actions flat color="transparent" v-if="promptingRegret">
            <v-spacer />
            <v-btn key="cancel" flat @click="promptingRegret = false"
              >Cancel</v-btn
            >
            <v-btn class="red darken-3 white--text" @click="submitRegretAll"
              >Regret all</v-btn
            >
          </v-card-actions>
          <v-card-actions flat color="transparent" v-else>
            <v-spacer />
            <v-tooltip left>
              <v-btn
                slot="activator"
                flat
                fab
                class="darken-3 red--text"
                @click="promptingRegret = true"
              >
                <v-icon>mdi-account-off</v-icon>
              </v-btn>
              <span>Mark all above as unsuccessful</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-layout>
      <ApplicationDialog
        v-if="!applicationsLoading && view"
        :id="parseInt(view)"
        @close="toggleView($event)"
      />
    </v-container>
  </v-content>
</template>

<script>
// Components
import ApplicationDialog from "@/components/client/applications/ApplicationDialog";
import ApplicationEventsList from "@/components/client/applications/ApplicationEventsList";

// Helper
import { mapState } from "vuex";

// Mixins
import { managedDocumentAttributes } from "@/mixins/managedDocumentAttributes";
import { managedQueryParameters } from "@/mixins/managedQueryParameters.js";

export default {
  mixins: [managedDocumentAttributes, managedQueryParameters],

  components: { ApplicationDialog, ApplicationEventsList },

  created() {
    this.documentAttributes.title = `Candidate Search – Leaply`;
    this.$store.dispatch("client/opportunities/fetchAll"),
      this.initializeSearch();
    if (this.search && this.search.length) this.submitSearch();
  },

  beforeDestroy() {
    this.$store.dispatch("client/applications/abandon");
    this.$store.dispatch("client/applications/resetState");
  },

  data() {
    return {
      search: "",
      view: null,
      promptingRegret: false,
    };
  },

  computed: {
    ...mapState("client/applications", {
      applications: "all",
      applicationsLoading: "loading",
      total: "total",
      page: "page",
      per: "rowsPerPage",
    }),
    opportunities() {
      return this.$store.getters["client/opportunities/all"];
    },
    multipleRegrettable() {
      // More than one active application
      if (
        _.filter(
          this.applications,
          (a) => !_.includes(["Unsuccessful", "Withdrawn"], a.status)
        ).length < 1
      ) {
        return false;
      }
      // Only one candidate
      if (_.uniq(this.applications.map((a) => a.profile.id)).length != 1) {
        return false;
      }

      return true;
    },
  },

  methods: {
    newTab(uri) {
      if (!uri) return;

      window.open(uri, "_blank");
    },
    initializeSearch() {
      if (
        typeof _.get(this, "initialQueryParameters['search'][0]") === "string"
      ) {
        this.search = this.initialQueryParameters["search"][0];
        return;
      }
    },

    canEditApplication(application) {
      return this.opportunities
        .map((o) => o.id)
        .includes(_.get(application, "opportunity.id"));
    },

    toggleView(id = null) {
      this.view = id;
      if (this.view === null) this.submitSearch();
    },

    submitSearch: _.debounce(function () {
      this.updateQueryParameters({ search: this.search });
      this.$store.dispatch("client/applications/resetState");

      if (this.search && this.search.length) {
        this.$store.dispatch("client/applications/search", {
          search: this.search,
        });
      }
    }, 400),

    submitRegretAll() {
      if (this.applications[0]) {
        const profileId = this.applications[0].profile.id;

        this.$http
          .put(`/client/profiles/${profileId}/regret`)
          .then((response) =>
            this.$store.dispatch(
              "snackbarSuccess",
              "All relevant applications have been updated"
            )
          )
          .catch((response) =>
            this.$store.dispatch(
              "snackbarError",
              "There was a problem. Please try again later or contact support."
            )
          )
          .finally(this.submitSearch);
      }
    },
  },
};
</script>

<style lang="scss">
.client.candidate-search.page .truncate {
  max-width: 70%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .fade-out-right {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &::after {
      content: " ";
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 60%;

      background: linear-gradient(to right, transparent 80%, #fff);
    }
  }
}
</style>
